import { useEffect, UIEvent } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { valueForOnlyNumericInputTextFields } from "utils/helpers";

import {
  selectDropDownsOptions,
  selectIsAddModalOpen,
  selectManufacturerModelForm,
  selectManufacturerModelsLoading,
} from "../redux/selector";
import { manufacturerModelActions } from "../redux/slice";
import { ManufacturerModelForm, ModelFormDropDownKey } from "../types";

export type ModelSelectOption = {
  value: string;
  label: string;
};

export type ModelFieldConfig = {
  key: keyof ManufacturerModelForm;
  label: string;
  type: "text" | "select";
  inputType?: "string" | "number";
  options?: ModelSelectOption[];
  dropDownKey?: ModelFormDropDownKey;
};

export type ModelCheckboxConfig = {
  key: keyof ManufacturerModelForm;
  label: string;
};

const P = "MANUFACTURER_MODELS.MODAL";
export const CHECKBOX_SIZE = 20;

export const MODEL_NUMBER_FIELDS = new Set<keyof ManufacturerModelForm>([
  "year",
  "chassisWidth",
  "chassisHeight",
  "maxWidth",
  "frontExtension",
  "licenseWeight",
  "chassisWeight",
  "frontWeelBase",
]);

const REQUIRED_FIELDS: Array<keyof ManufacturerModelForm> = [
  "manufacturerID",
  "year",
  "modelDesc",
];

const FIELD_CONFIGS: ModelFieldConfig[] = [
  {
    key: "manufacturerID",
    label: "MANUFACTURER_ID",
    type: "select",
    dropDownKey: "manufacturers",
  },
  { key: "year", label: "YEAR", type: "text", inputType: "number" },
  {
    key: "modelDesc",
    label: "MODEL_DESCRIPTION",
    type: "select",
    dropDownKey: "superModels",
  },
  {
    key: "chassisWidth",
    label: "CHASSIS_WIDTH",
    type: "text",
    inputType: "number",
  },
  {
    key: "chassisHeight",
    label: "CHASSIS_HEIGHT",
    type: "text",
    inputType: "number",
  },
  {
    key: "baseBeamType",
    label: "BASE_BEAM_TYPE",
    type: "text",
    inputType: "string",
  },
  { key: "maxWidth", label: "MAX_WIDTH", type: "text", inputType: "number" },
  {
    key: "frontExtension",
    label: "FRONT_OVERHANG",
    type: "text",
    inputType: "number",
  },
  {
    key: "licenseWeight",
    label: "LICENSED_WEIGHT",
    type: "text",
    inputType: "number",
  },
  { key: "weelDrive", label: "DRIVE", type: "text", inputType: "string" },
  {
    key: "baseBeamTypeRoof",
    label: "OPENING_BASE",
    type: "text",
    inputType: "string",
  },
  {
    key: "chassisWeight",
    label: "CHASSIS_WEIGHT",
    type: "text",
    inputType: "number",
  },
  {
    key: "frontWeelBase",
    label: "FRONT_AXLE_DISTANCE",
    type: "text",
    inputType: "number",
  },
  {
    key: "registrationInstruction",
    label: "REGISTRATION_INSTRUCTION",
    type: "text",
    inputType: "string",
  },
];

const CHECKBOX_FIELDS: ModelCheckboxConfig[] = [
  { key: "sideLighting", label: "SIDE_LIGHTING" },
  { key: "sideGuard", label: "SIDE_GUARD" },
  { key: "mudguard", label: "MUDGUARD" },
  { key: "extendableChassis", label: "EXTENDABLE_CHASSIS" },
];

export const sanitizeModelFormValue = (
  name: keyof ManufacturerModelForm,
  value: string | boolean,
) => {
  if (typeof value !== "string") return value;
  if (MODEL_NUMBER_FIELDS.has(name)) {
    return valueForOnlyNumericInputTextFields(value);
  }
  return value;
};

export const useAddModelModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const form = useSelector(selectManufacturerModelForm)!;
  const isLoading = useSelector(selectManufacturerModelsLoading);
  const isOpen = useSelector(selectIsAddModalOpen)!;
  const { manufacturers, superModels } = useSelector(selectDropDownsOptions);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(manufacturerModelActions.getFormDropDownList("manufacturers"));
    dispatch(manufacturerModelActions.getFormDropDownList("superModels"));
  }, [isOpen, dispatch]);

  const fields = FIELD_CONFIGS.map((field) => {
    if (field.key === "manufacturerID") {
      return {
        ...field,
        options: manufacturers.items.map(({ id, name }) => ({
          value: String(id),
          label: name,
        })),
      };
    }
    if (field.key === "modelDesc") {
      return {
        ...field,
        options: superModels.items.map(({ name }) => ({
          value: name,
          label: name,
        })),
      };
    }
    return field;
  });

  const onClose = () => {
    if (isLoading) return;
    dispatch(manufacturerModelActions.resetForm());
    dispatch(manufacturerModelActions.closeAddModal());
  };

  const setField = (
    name: keyof ManufacturerModelForm,
    value: string | boolean,
  ) =>
    dispatch(
      manufacturerModelActions.setFormField({
        name,
        value: sanitizeModelFormValue(name, value),
      }),
    );

  const toggleCheckbox = (key: keyof ManufacturerModelForm) => {
    if (isLoading) return;
    setField(key, !Boolean(form[key]));
  };

  const isValid = REQUIRED_FIELDS.every((key) =>
    Boolean(String(form[key] || "").trim()),
  );

  const onSubmit = () => {
    if (!isValid) return;
    dispatch(manufacturerModelActions.addManufacturerModel(form));
    dispatch(manufacturerModelActions.closeAddModal());
  };

  const onDropDownScroll = (dropDownKey?: ModelFormDropDownKey) => {
    if (!dropDownKey) return;
    dispatch(manufacturerModelActions.getFormDropDownNextPage(dropDownKey));
  };

  const hasMoreDropDownItems = (dropDownKey?: ModelFormDropDownKey) => {
    if (!dropDownKey) return false;
    const dropDown =
      dropDownKey === "manufacturers" ? manufacturers : superModels;
    return (
      !dropDown.loading &&
      dropDown.meta.currentPage < dropDown.meta.totalPages
    );
  };

  const handleMenuScroll =
    (dropDownKey?: ModelFormDropDownKey) =>
    (event: UIEvent<HTMLElement>) => {
      if (!dropDownKey || !hasMoreDropDownItems(dropDownKey)) return;
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - (scrollTop + clientHeight) <= 2) {
        onDropDownScroll(dropDownKey);
      }
    };

  const fieldLabel = (label: string) => t(`${P}.FIELDS.${label}`);
  const placeholder = (label: string) => t(`${P}.PLACEHOLDERS.ENTER_${label}`);
  const fieldValue = (key: keyof ManufacturerModelForm) =>
    String(form[key] || "");

  return {
    t,
    form,
    isLoading,
    isOpen,
    fields,
    checkboxFields: CHECKBOX_FIELDS,
    isValid,
    addTitle: t(`${P}.ADD_TITLE`),
    onClose,
    setField,
    toggleCheckbox,
    onSubmit,
    handleMenuScroll,
    fieldLabel,
    placeholder,
    fieldValue,
  };
};
